import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useSchoolQueries from "../store/schools/schoolsQueries";
import StudentTable from "../shared/components/Table/StudentTable";
import SchoolDetailsPopup from "../shared/components/SchoolDetailsPopup/SchoolDetailsPopup";
import Pagination from "../shared/components/Pagination/Pagination";
import search_icon from "../assets/images/search_icon.svg";
import loader from "../assets/gifs/loader.gif";
import "./Schools.css";

const Schools = () => {
  const {
    schools,
    totalCount,
    currentPage,
    itemsPerPage,
    isLoading,
    error,
    handlePageChange,
    handleLimitChange,
    handleSearchQueryChange,
    refetch,
  } = useSchoolQueries(1, 100);

  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearchQueryChange(searchInput);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const handleRowClick = (school, action) => {
    if (action === "viewStudents") {
      handleViewStudents(school);
      return;
    }
    
    // API response already matches the required schema
    const transformedSchool = {
      ...school,
      // Ensure all required fields exist with defaults
      school_type: school.school_type || "Public",
      board: school.board || "",
      branch_name: school.branch_name || "",
      address: school.address || "",
      city: school.city || "",
      state: school.state || "",
      country: school.country || "India",
      pincode: school.pincode || "",
      isd_code: school.isd_code || "+91",
      phone: school.phone || "",
      email: school.email || "",
      activation_code: school.activation_code || "",
      fee: school.fee || "",
      tier: school.tier || "",
      category: school.category || "",
      primary_contact_info: {
        ...school.primary_contact_info,
        name: school.primary_contact_info?.name || "",
        isd_code: school.primary_contact_info?.isd_code || "+91",
        phone: school.primary_contact_info?.phone || "",
        email: school.primary_contact_info?.email || "",
      },
      secondary_contact_info: {
        ...school.secondary_contact_info,
        name: school.secondary_contact_info?.name || "",
        isd_code: school.secondary_contact_info?.isd_code || "+91",
        phone: school.secondary_contact_info?.phone || "",
        email: school.secondary_contact_info?.email || "",
        designation: school.secondary_contact_info?.designation || "",
      },
      total_student_count: school.total_student_count || 0,
      total_teacher_count: school.total_teacher_count || 0,
      other_staff_count: school.other_staff_count || 0,
      grade_wise_count: {
        ...school.grade_wise_count,
        nursery: school.grade_wise_count?.nursery || 0,
        lkg: school.grade_wise_count?.lkg || 0,
        ukg: school.grade_wise_count?.ukg || 0,
        grade_1: school.grade_wise_count?.grade_1 || 0,
        grade_2: school.grade_wise_count?.grade_2 || 0,
        grade_3: school.grade_wise_count?.grade_3 || 0,
        grade_4: school.grade_wise_count?.grade_4 || 0,
        grade_5: school.grade_wise_count?.grade_5 || 0,
        grade_6: school.grade_wise_count?.grade_6 || 0,
        grade_7: school.grade_wise_count?.grade_7 || 0,
        grade_8: school.grade_wise_count?.grade_8 || 0,
        grade_9: school.grade_wise_count?.grade_9 || 0,
        grade_10: school.grade_wise_count?.grade_10 || 0,
        grade_11: school.grade_wise_count?.grade_11 || 0,
        grade_12: school.grade_wise_count?.grade_12 || 0,
      },
    };
    setSelectedSchool(transformedSchool);
    setIsPopupVisible(true);
  };

  const navigate = useNavigate();

  const handleViewStudents = (school) => {
    navigate(`/students?schoolId=${school.school_id}`);
  };

  const columns = [
    { key: "school_id", label: "SCHOOL ID" },
    { key: "school_name", label: "SCHOOL NAME" },
    { key: "activation_code", label: "INVITATION CODE" },
    { key: "address", label: "ADDRESS" },
    { key: "onboard", label: "ONBOARDED" },
    { key: "viewStudents", label: "" },
  ];

  if (error) {
    return <div className="error-message">Error: {error.message}</div>;
  }

  return (
    <div className="schools-mainCont">
      <div className="headerCont">
        <div className="titlecont">
          <h3>Schools</h3>
        </div>
      </div>
      <div className="filters-wrapper">
        <div className="search-wrapper">
          <img src={search_icon} alt="Search" className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, phone, invitation code, school ID"
            value={searchInput}
            onChange={handleSearch}
            className="schools-search-input"
          />
        </div>
        <Pagination
          totalItems={totalCount}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          itemsPerPageOptions={[50, 100, 500, 1000]}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
        />
      </div>
      <div className="tableCont">
        {isLoading ? (
          <div className="loaderCont">
            <img
              src={loader}
              alt="Loading spinner"
              style={{ width: "200px", height: "200px", margin: "auto" }}
            />
          </div>
        ) : (
          <StudentTable
            data={schools}
            columns={columns}
            showRowSelector={false}
            onRowClick={handleRowClick}
          />
        )}
      </div>
      <SchoolDetailsPopup
        school={selectedSchool}
        isVisible={isPopupVisible}
        onClose={() => {
          setIsPopupVisible(false);
          setSelectedSchool(null);
        }}
      />
    </div>
  );
};

export default Schools;
